import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider
} from '@mui/material';
import { Controller, useFormContext, FieldErrors } from 'react-hook-form';
import { ParentRegisterData } from '../../../../../types/auth';

const LinkStudentStep = () => {
  const { control, formState: { errors }, watch, setValue } = useFormContext<ParentRegisterData>();
  const childOption = watch('childOption') || 'existing';

  // Properly typed errors
  const childDataErrors = errors.childData as FieldErrors<ParentRegisterData['childData']> | undefined;

  return (
    <>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium', color: 'secondary.main' }}>
        Link to Student Account
      </Typography>

      <Controller
        name="childOption"
        control={control}
        defaultValue="existing"
        render={({ field }) => (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How would you like to link your student?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={childOption === 'existing' ? 3 : 1}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    border: childOption === 'existing' ? '2px solid' : '1px solid',
                    borderColor: childOption === 'existing' ? 'primary.main' : 'divider',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    field.onChange('existing');
                    // Clear new student fields
                    setValue('childData.password', '');
                    setValue('childData.confirmPassword', '');
                    setValue('childData.firstName', '');
                    setValue('childData.lastName', '');
                    setValue('childData.username', '');
                    setValue('childData.school', '');
                    setValue('childData.grade', undefined);
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    Existing Student
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Link to an existing student account
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={childOption === 'new' ? 3 : 1}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    border: childOption === 'new' ? '2px solid' : '1px solid',
                    borderColor: childOption === 'new' ? 'secondary.main' : 'divider',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    field.onChange('new');
                    // Clear existing student field
                    setValue('childData.email', '');
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    New Student
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create a new student account
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      />

      {childOption === 'existing' ? (
        <Controller
          name="childData.email"
          control={control}
          rules={{
            required: 'Student email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              required
              fullWidth
              id="studentEmail"
              label="Student Email Address"
              autoComplete="email"
              error={!!childDataErrors?.email}
              helperText={childDataErrors?.email?.message || 'Enter the email address of the existing student account'}
              inputProps={{ 'data-testid': 'parent-child-email-input' }}
            />
          )}
        />
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a new student account for your child
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="secondary.main" sx={{ mb: 1, fontWeight: 'bold' }}>
              Account Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Controller
            name="childData.email"
            control={control}
            rules={{
              required: 'Student email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                id="newStudentEmail"
                label="Student Email Address"
                autoComplete="email"
                error={!!childDataErrors?.email}
                helperText={childDataErrors?.email?.message}
                inputProps={{ 'data-testid': 'parent-newChild-email-input' }}
              />
            )}
          />

          <Controller
            name="childData.password"
            control={control}
            rules={{
              required: 'Password is required for new student',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                type="password"
                id="newStudentPassword"
                label="Student Password"
                error={!!childDataErrors?.password}
                helperText={childDataErrors?.password?.message}
                inputProps={{ 'data-testid': 'parent-newChild-password-input' }}
              />
            )}
          />

          <Controller
            name="childData.confirmPassword"
            control={control}
            rules={{
              required: 'Please confirm the password',
              validate: (value) => {
                const password = watch('childData.password');
                return value === password || 'Passwords do not match';
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                type="password"
                id="newStudentConfirmPassword"
                label="Confirm Student Password"
                error={!!childDataErrors?.confirmPassword}
                helperText={childDataErrors?.confirmPassword?.message}
                inputProps={{ 'data-testid': 'parent-newChild-confirmPassword-input' }}
              />
            )}
          />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="secondary.main" sx={{ mb: 1, fontWeight: 'bold' }}>
              Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Controller
            name="childData.firstName"
            control={control}
            rules={{
              required: 'First name is required'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                id="newStudentFirstName"
                label="Student First Name"
                error={!!childDataErrors?.firstName}
                helperText={childDataErrors?.firstName?.message}
                inputProps={{ 'data-testid': 'parent-newChild-firstName-input' }}
              />
            )}
          />

          <Controller
            name="childData.lastName"
            control={control}
            rules={{
              required: 'Last name is required'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                id="newStudentLastName"
                label="Student Last Name"
                error={!!childDataErrors?.lastName}
                helperText={childDataErrors?.lastName?.message}
                inputProps={{ 'data-testid': 'parent-newChild-lastName-input' }}
              />
            )}
          />

          <Controller
            name="childData.username"
            control={control}
            rules={{
              required: 'Username is required',
              minLength: {
                value: 3,
                message: 'Username must be at least 3 characters'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                id="newStudentUsername"
                label="Student Username"
                error={!!childDataErrors?.username}
                helperText={childDataErrors?.username?.message}
                inputProps={{ 'data-testid': 'parent-newChild-username-input' }}
              />
            )}
          />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="secondary.main" sx={{ mb: 1, fontWeight: 'bold' }}>
              Student Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Controller
            name="childData.school"
            control={control}
            rules={{
              required: 'School is required'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                required
                fullWidth
                id="newStudentSchool"
                label="Student School"
                error={!!childDataErrors?.school}
                helperText={childDataErrors?.school?.message}
                inputProps={{ 'data-testid': 'parent-newChild-school-input' }}
              />
            )}
          />

          <Controller
            name="childData.grade"
            control={control}
            rules={{
              required: 'Grade is required',
              min: {
                value: 5,
                message: 'Grade must be between 5 and 8'
              },
              max: {
                value: 8,
                message: 'Grade must be between 5 and 8'
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val ? Number(val) : undefined);
                }}
                margin="normal"
                required
                fullWidth
                type="number"
                id="newStudentGrade"
                label="Student Grade"
                inputProps={{ 'data-testid': 'parent-newChild-grade-input', min: 5, max: 8 }}
                error={!!childDataErrors?.grade}
                helperText={childDataErrors?.grade?.message || 'Grades 5-8'}
              />
            )}
          />
          </Box>
        </>
      )}
    </>
  );
};

export default LinkStudentStep;
